import { useEffect, useRef, useState } from 'react';

/**
 * Something to read while the assistant is thinking.
 *
 * The panel used to show nothing at all between a question being sent and the first word coming
 * back, which on a slow answer is ten seconds of a window that looks broken. Dots alone fixed the
 * "is it alive" half and not the "how long is this going to be" half: a pulse that has not changed
 * in eight seconds stops reading as progress and starts reading as a hang.
 *
 * So the word changes, and the clock runs. Neither is telemetry - Daive reports nothing about how
 * far through it is, and pretending otherwise with a progress bar would be a lie. What they are is
 * evidence that this end is still running, which is the honest thing to show while waiting on
 * somebody else's server (user, 2026-09-09).
 */

/**
 * The words themselves.
 *
 * One or two of them, because the row sits inline beside the dots and a long phrase makes the
 * panel jump every time it changes. Deliberately not a description of what the model is doing:
 * nothing here knows that, and a made-up "searching records" beside a lookup that never happened
 * would be worse than a word that is obviously just keeping you company.
 */
export const WORKING_WORDS: string[] = [
    'thinking',
    'pondering',
    'ruminating',
    'cogitating',
    'deliberating',
    'mulling it over',
    'percolating',
    'noodling',
    'puzzling',
    'wrangling',
    'untangling',
    'rummaging',
    'sifting',
    'whirring',
    'ticking over',
    'chewing it over',
    'gathering itself',
    'herding thoughts',
    'consulting the ether',
    'having a good think',
    'marshalling an answer',
    'beavering away',
];

/** How long each word stays before the next one. Long enough to read, short enough to notice. */
const WORD_MS: number = 2000;

/** How often the clock ticks once it is showing. */
const CLOCK_MS: number = 1000;

/**
 * How long a wait has to be before the seconds appear.
 *
 * Most answers start inside this, and a counter that flashes up and vanishes is worse than no
 * counter. It earns its place only once the wait is long enough to wonder about.
 */
const CLOCK_AFTER_MS: number = 3000;

/**
 * The words for one wait, in an order nobody sees twice running.
 *
 * `thinking` is always first because it is the one that actually says what is happening, and the
 * first word is the one most waits will ever show. The rest are shuffled so a second question does
 * not read like a repeat of the first.
 */
function orderedWords(): string[] {
    const rest: string[] = WORKING_WORDS.slice(1);
    for (let index = rest.length - 1; index > 0; index--) {
        const swap: number = Math.floor(Math.random() * (index + 1));
        [rest[index], rest[swap]] = [rest[swap], rest[index]];
    }
    return [WORKING_WORDS[0], ...rest];
}

export interface WorkingStatus {
    /** The word to show. Changes every couple of seconds. */
    word: string;
    /** Whole seconds waited, or 0 until the wait is long enough to be worth counting. */
    seconds: number;
}

/**
 * Track one wait, and reset when the next one starts.
 *
 * @param waiting whether the assistant is working on something right now
 * @return the word and the elapsed seconds to put on screen
 */
export function useWorkingStatus(waiting: boolean): WorkingStatus {
    const [word, setWord] = useState<string>(WORKING_WORDS[0]);
    const [seconds, setSeconds] = useState<number>(0);
    const words = useRef<string[]>(WORKING_WORDS);

    useEffect((): (() => void) | void => {
        if (!waiting) {
            // Reset rather than freeze, so the next question opens on `thinking` and a zeroed
            // clock instead of resuming wherever the last one was interrupted.
            setWord(WORKING_WORDS[0]);
            setSeconds(0);
            return;
        }

        words.current = orderedWords();
        setWord(words.current[0]);
        setSeconds(0);

        let position: number = 0;
        const wording: ReturnType<typeof setInterval> = setInterval((): void => {
            position = (position + 1) % words.current.length;
            setWord(words.current[position]);
        }, WORD_MS);

        const startedAt: number = Date.now();
        const clock: ReturnType<typeof setInterval> = setInterval((): void => {
            const waited: number = Date.now() - startedAt;
            setSeconds(waited < CLOCK_AFTER_MS ? 0 : Math.floor(waited / 1000));
        }, CLOCK_MS);

        return (): void => {
            clearInterval(wording);
            clearInterval(clock);
        };
    }, [waiting]);

    return { word: word, seconds: seconds };
}
